import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiChevronDown } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import type { PlanStep } from './chat.types';
import '../../styles/ThinkingBlock.css';

/* ── Tool label map ── */
const TOOL_LABELS: Record<string, string> = {
  thinking: 'thinking',
  get_query: 'get_query',
  execute_sql_query: 'execute sql query',
};

/* ── Phase config ── */
const PHASE_LABELS: Record<string, { label: string; color: string }> = {
  thinking:  { label: 'Thinking',  color: '#a78bfa' },
  executing: { label: 'Executing', color: '#3b82f6' },
  observing: { label: 'Observing', color: '#22c55e' },
};

/* ── Elapsed timer ── */
function useElapsedTime(isActive: boolean): string {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (isActive) {
      startRef.current = Date.now();
      setElapsed(0);
      const interval = setInterval(() => {
        setElapsed(Math.round((Date.now() - startRef.current) / 100) / 10);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isActive]);

  return elapsed > 0 ? `${elapsed.toFixed(1)}s` : '';
}

/* ── Plan Step Icon ── */
const PlanStepIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'complete':
      return <span className="tb-step__icon tb-step__icon--complete">&#10003;</span>;
    case 'active':
      return <span className="tb-step__icon tb-step__icon--active" />;
    default:
      return <span className="tb-step__icon tb-step__icon--pending" />;
  }
};

/* ── Plan Steps Display ── */
const PlanStepsDisplay: React.FC<{ steps: PlanStep[] }> = ({ steps }) => {
  const completed = steps.filter((s) => s.status === 'complete').length;
  return (
    <div className="tb-steps">
      <div className="tb-steps__header">
        <FiChevronDown size={11} style={{ color: 'var(--text-muted)' }} />
        <span className="tb-steps__title">Plan Steps</span>
        <span className="tb-steps__count">{completed}/{steps.length}</span>
      </div>
      {steps.map((step) => (
        <div key={step.step} className={`tb-step tb-step--${step.status}`}>
          <PlanStepIcon status={step.status} />
          <div className="tb-step__info">
            <span className="tb-step__num">{step.step}.</span>
            <span className="tb-step__label">{step.title}</span>
            {step.tool && step.status !== 'pending' && (
              <span className="tb-step__tool">{step.tool}</span>
            )}
            {step.status === 'complete' && step.resultSummary && (
              <span className="tb-step__summary">
                {step.resultSummary.slice(0, 80)}{step.resultSummary.length > 80 ? '...' : ''}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── Main ThinkingBlock Component ── */

export interface ThinkingBlockProps {
  thoughtText: string;
  currentTool?: string;
  currentToolLabel?: string;
  isActiveThinking: boolean;
  hasAnswer: boolean;
  planSteps?: PlanStep[];
  phase?: string;
  stepNumber?: number;
  totalSteps?: number;
}

const ThinkingBlock: React.FC<ThinkingBlockProps> = ({
  thoughtText, currentTool, currentToolLabel,
  isActiveThinking, hasAnswer, planSteps, phase,
}) => {
  const [expanded, setExpanded] = useState(!hasAnswer);
  const elapsedTime = useElapsedTime(isActiveThinking);
  const textEndRef = useRef<HTMLDivElement>(null);

  const toolLabel = currentToolLabel
    || (currentTool ? (TOOL_LABELS[currentTool] || currentTool) : '');

  const phaseInfo = phase ? PHASE_LABELS[phase] : null;

  // Scroll thought text to bottom while streaming — only within .tb__text, never the outer container
  useEffect(() => {
    if (isActiveThinking && expanded && textEndRef.current) {
      const parent = textEndRef.current.parentElement;
      if (parent) parent.scrollTop = parent.scrollHeight;
    }
  }, [thoughtText, isActiveThinking, expanded]);

  return (
    <div className={`tb ${isActiveThinking ? 'tb--active' : ''}`}>
      {/* Header */}
      <div className="tb__header" onClick={() => setExpanded((p) => !p)}>
        <div className="tb__header-left">
          <RiSparklingFill
            size={13}
            className={`tb__spark ${isActiveThinking ? 'tb__spark--active' : ''}`}
          />
          <span className="tb__title">
            {isActiveThinking ? 'Thinking' : 'Thought process'}
          </span>
          {isActiveThinking && <span className="tb__dot" />}
        </div>

        <div className="tb__header-right">
          {isActiveThinking && phaseInfo && (
            <span className="tb__phase" style={{ color: phaseInfo.color }}>
              {phaseInfo.label}
            </span>
          )}
          {toolLabel && (
            <span className="tb__tool">
              {isActiveThinking && <span className="tb__tool-dot" />}
              {toolLabel}
            </span>
          )}
          {elapsedTime && (
            <span className="tb__timer">{elapsedTime}</span>
          )}
          <FiChevronDown
            size={14}
            className={`tb__chevron ${expanded ? 'tb__chevron--open' : ''}`}
          />
        </div>
      </div>

      {/* Expandable content */}
      <SafeAnimatePresence>
        {expanded && (thoughtText || (planSteps && planSteps.length > 0)) && (
          <motion.div
            className="tb__content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {thoughtText && (
              <div className="tb__text">
                {thoughtText}
                {isActiveThinking && <span className="chat-msg__cursor" />}
                <div ref={textEndRef} />
              </div>
            )}
            {planSteps && planSteps.length > 0 && (
              <PlanStepsDisplay steps={planSteps} />
            )}
          </motion.div>
        )}
      </SafeAnimatePresence>
    </div>
  );
};

export default React.memo(ThinkingBlock);
